/**
 * WriterStatsPanel.jsx — unified stats grid powering Scribe Stats 🖋️,
 * Reader Stats and Admin Stats sections on the Profile / Journey pages.
 *
 * Feb 12 2026 user spec:
 *   "Wire all stats to look like this with appropriate stats to match
 *    each profile. Admin, reader, writers (Scribe 🖋️)."
 *
 * Pulls from `/api/admin/users/{user_id}/profile` — the endpoint serves
 * Scribe output + Reader behaviour + Admin platform totals in a single
 * payload, so we slice the sections shown based on the `mode` prop.
 *
 * Modes:
 *   • mode="writer" (default)  → Published output, Readership,
 *                                 Reader/Listener behaviour, Earnings.
 *   • mode="reader"            → Reader behaviour, Reader earnings
 *                                 (Reader XP / Level / Coins).
 *   • mode="admin"             → Everything Scribe sees + Platform
 *                                 totals (users, queues, content, ledger).
 */
import React, { useEffect, useState } from 'react';
import {
  BookOpen, Headphones, Film, Layers, Users, TrendingUp,
  Award, Trophy, Sparkles, ShieldCheck, Crown, Inbox, FileCheck,
  Coins, FileStack, Share2,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import ShareScribeMilestone from './ShareScribeMilestone';
import StatBreakdownModal from './StatBreakdownModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatTile = ({ label, value, Icon, accent = 'amber', testid, onClick }) => {
  const palette = {
    amber:   'border-amber-500/30   bg-amber-500/10   text-amber-200',
    fuchsia: 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-200',
    indigo:  'border-indigo-500/30  bg-indigo-500/10  text-indigo-200',
    emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
    cyan:    'border-cyan-500/30    bg-cyan-500/10    text-cyan-200',
    pink:    'border-pink-500/30    bg-pink-500/10    text-pink-200',
    rose:    'border-rose-500/30    bg-rose-500/10    text-rose-200',
    violet:  'border-violet-500/30  bg-violet-500/10  text-violet-200',
  }[accent] || 'border-slate-500/30 bg-slate-500/10 text-slate-200';
  const display = (value === undefined || value === null) ? 0 : value;
  // Tiles are clickable by default (drill into breakdown). When no
  // onClick is provided we still render a non-interactive div so the
  // hover affordance doesn't lie.
  const Tag = onClick ? 'button' : 'div';
  const interactiveCls = onClick
    ? 'hover:brightness-125 hover:scale-[1.02] active:scale-100 transition-transform cursor-pointer text-left w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60'
    : '';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`rounded-xl border ${palette} p-3 ${interactiveCls}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3 opacity-80" />
        <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">{label}</p>
      </div>
      <p className="text-2xl font-black text-white tabular-nums mt-0.5">
        {typeof display === 'number' ? display.toLocaleString() : display}
      </p>
    </Tag>
  );
};

const WriterStatsPanel = ({ userId, mode = 'writer' }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  // Which breakdown tile is open. null = closed. Each StatTile sets this
  // to its `kind` on click; the modal reads it and fetches the rows.
  const [breakdownKind, setBreakdownKind] = useState(null);
  const openBreakdown = (kind) => setBreakdownKind(kind);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/admin/users/${userId}/profile`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) { setErr(d.detail || `Could not load stats (${r.status})`); return; }
        setData(d);
        setErr('');
      } catch (e) {
        if (!cancelled) setErr(e?.message || 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading || (!data && !err)) {
    return (
      <div className="space-y-3 animate-pulse" data-testid="journey-writer-stats-panel-loading">
        <div className="h-4 w-32 bg-slate-800 rounded" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-16 bg-slate-800/70 border border-slate-700 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <p className="text-xs text-rose-300" data-testid="journey-stats-error">
        Couldn&apos;t load your stats — {err}
      </p>
    );
  }

  const o = data.output || {};
  const b = data.behaviour || {};
  const e = data.earnings || {};
  const r = data.reader || {};
  const p = data.platform || null;
  const mg = data.merge_group || null;

  // ── Merged-accounts banner (Feb 12 2026 spec)
  // When the target user is linked to another account via a
  // `merge_group_id`, the API sums all stats across the group.
  // We surface a small banner so the viewer knows the totals
  // they see are combined.
  const MergeBanner = mg?.in_group ? (
    <div
      className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 flex items-center gap-2"
      data-testid="journey-stats-merge-banner"
    >
      <Crown className="w-3.5 h-3.5 flex-shrink-0" />
      <span>
        <span className="font-bold uppercase tracking-widest mr-1">Merged stats</span>
        across {mg.member_count} linked accounts —{' '}
        {(mg.members || [])
          .map((m) => m.name || m.email)
          .filter(Boolean)
          .join(' · ')}
      </span>
    </div>
  ) : null;

  // ── Scribe sections (writer + admin both render these) ──────────
  const canShareScribeMilestone =
    (mode === 'writer' || mode === 'admin') && (e.writer_xp || 0) > 0;

  const PublishedOutput = (
    <section data-testid="journey-stats-output">
      <div className="flex items-center justify-between mb-2 gap-2">
        <h3 className="eyebrow text-amber-300">Published output 🖋️</h3>
        {canShareScribeMilestone && (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/30 text-amber-200 text-[10px] font-bold uppercase tracking-widest transition-colors"
            data-testid="share-scribe-milestone-btn"
            aria-label="Share Scribe milestone"
          >
            <Share2 className="w-3 h-3" /> Share milestone
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatTile label="Ebooks" value={o.ebooks} Icon={BookOpen} accent="amber" testid="journey-stats-ebooks" onClick={() => openBreakdown('ebooks')} />
        <StatTile label="Audiobooks" value={o.audiobook_series} Icon={Headphones} accent="fuchsia" testid="journey-stats-audiobooks" onClick={() => openBreakdown('audiobooks')} />
        <StatTile label="Cinematic" value={o.cinematic_series} Icon={Film} accent="pink" testid="journey-stats-cinematic" onClick={() => openBreakdown('cinematic')} />
        <StatTile label="Book Series" value={o.text_series} Icon={Layers} accent="indigo" testid="journey-stats-text-series" onClick={() => openBreakdown('book_series')} />
        <StatTile label="Text Episodes" value={o.text_episodes} Icon={Layers} accent="indigo" testid="journey-stats-text-episodes" onClick={() => openBreakdown('text_episodes')} />
        <StatTile label="Chapters" value={o.chapters} Icon={Layers} accent="cyan" testid="journey-stats-chapters" onClick={() => openBreakdown('chapters')} />
      </div>
      <p className="text-[11px] text-slate-400 mt-2 tabular-nums" data-testid="journey-stats-words">
        {o.words_published?.toLocaleString() || 0} words published
        {o.xp_from_words ? (
          <span className="text-amber-300/80"> · +{o.xp_from_words.toLocaleString()} Scribe XP earned 🖋️</span>
        ) : null}
      </p>
      <p className="text-[10px] text-slate-500 mt-0.5" data-testid="journey-stats-xp-rule">
        Rule: 100 XP per 1,000 words published.
      </p>
    </section>
  );

  const Readership = (
    <section data-testid="journey-stats-readership">
      <h3 className="eyebrow text-cyan-300 mb-2">Readership</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Readers" value={o.distinct_readers} Icon={Users} accent="cyan" testid="journey-stats-readers" onClick={() => openBreakdown('readers')} />
        <StatTile label="Followers" value={o.followers} Icon={Users} accent="emerald" testid="journey-stats-followers" onClick={() => openBreakdown('followers')} />
        <StatTile label="Retention" value={`${o.retention_pct || 0}%`} Icon={TrendingUp} accent="emerald" testid="journey-stats-retention" />
        <StatTile label="Episode finishes" value={o.text_episode_finishes} Icon={Sparkles} accent="indigo" testid="journey-stats-episode-finishes" onClick={() => openBreakdown('episode_finishes')} />
      </div>
    </section>
  );

  const ReaderBehaviour = (
    <section data-testid="journey-stats-behaviour">
      <h3 className="eyebrow text-fuchsia-300 mb-2">As a reader / listener</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Books opened" value={b.books_opened} Icon={BookOpen} accent="amber" testid="journey-stats-books-opened" onClick={() => openBreakdown('books_opened')} />
        <StatTile label="Completed" value={b.books_completed} Icon={BookOpen} accent="emerald" testid="journey-stats-books-completed" onClick={() => openBreakdown('books_completed')} />
        <StatTile label="Listened (min)" value={b.listening_minutes} Icon={Headphones} accent="fuchsia" testid="journey-stats-listening-min" onClick={() => openBreakdown('listening')} />
        <StatTile label="Streak best" value={b.streak_best} Icon={TrendingUp} accent="pink" testid="journey-stats-streak-best" />
      </div>
    </section>
  );

  const ScribeEarnings = (
    <section data-testid="journey-stats-earnings">
      <h3 className="eyebrow text-amber-300 mb-2">Scribe earnings 🖋️</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Scribe XP" value={e.writer_xp} Icon={Sparkles} accent="amber" testid="journey-stats-writer-xp" />
        <StatTile label="Scribe level" value={e.writer_level} Icon={Award} accent="amber" testid="journey-stats-writer-level" />
        <StatTile label="Coin balance" value={e.coin_balance} Icon={Trophy} accent="emerald" testid="journey-stats-coin-balance" />
        <StatTile label="Lifetime coins" value={e.lifetime_coins_earned} Icon={Trophy} accent="emerald" testid="journey-stats-lifetime-coins" />
      </div>
    </section>
  );

  // ── Reader-only earnings (Reader XP / Coins from reader_stats) ──
  const ReaderEarnings = (
    <section data-testid="journey-stats-reader-earnings">
      <h3 className="eyebrow text-indigo-300 mb-2">Reader earnings</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Reader XP" value={r.reader_xp} Icon={Sparkles} accent="indigo" testid="journey-stats-reader-xp" />
        <StatTile label="Reader level" value={r.reader_level} Icon={Award} accent="indigo" testid="journey-stats-reader-level" />
        <StatTile label="Coin balance" value={r.reader_coin_balance} Icon={Coins} accent="cyan" testid="journey-stats-reader-coins" />
        <StatTile label="Lifetime coins" value={r.reader_lifetime_coins} Icon={Trophy} accent="cyan" testid="journey-stats-reader-lifetime-coins" />
      </div>
      {r.reader_evolution ? (
        <p className="text-[11px] text-slate-400 mt-2">Evolution tier: <span className="text-indigo-200 font-bold">{r.reader_evolution}</span></p>
      ) : null}
    </section>
  );

  // ── Admin-only: platform totals ─────────────────────────────────
  const PlatformTotals = p ? (
    <section data-testid="journey-stats-platform">
      <h3 className="eyebrow text-violet-300 mb-2 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Platform totals
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Total users" value={p.total_users} Icon={Users} accent="violet" testid="journey-stats-total-users" />
        <StatTile label="Total scribes" value={p.total_scribes} Icon={Sparkles} accent="amber" testid="journey-stats-total-scribes" />
        <StatTile label="Kings" value={p.total_kings} Icon={Crown} accent="rose" testid="journey-stats-total-kings" />
        <StatTile label="Ambassadors" value={p.total_ambassadors} Icon={Award} accent="cyan" testid="journey-stats-total-ambassadors" />
        <StatTile label="Pending uploads" value={p.pending_uploads} Icon={Inbox} accent="amber" testid="journey-stats-pending-uploads" />
        <StatTile label="Scribe applications" value={p.pending_scribe_apps} Icon={FileCheck} accent="fuchsia" testid="journey-stats-pending-apps" />
        <StatTile label="Total content" value={p.total_content} Icon={FileStack} accent="indigo" testid="journey-stats-total-content" />
        <StatTile label="XP awarded" value={p.total_xp_awarded} Icon={Sparkles} accent="amber" testid="journey-stats-total-xp" />
        <StatTile label="Coins earned" value={p.total_coins_earned} Icon={Trophy} accent="emerald" testid="journey-stats-total-coins" />
        <StatTile label="Payouts paid (£)" value={`£${(p.total_payouts_gbp || 0).toLocaleString()}`} Icon={Coins} accent="emerald" testid="journey-stats-total-payouts" />
      </div>
    </section>
  ) : null;

  // ── Compose sections per mode ───────────────────────────────────
  let body;
  if (mode === 'reader') {
    body = (
      <>
        {ReaderBehaviour}
        {ReaderEarnings}
      </>
    );
  } else if (mode === 'admin') {
    body = (
      <>
        {PlatformTotals}
        {PublishedOutput}
        {Readership}
        {ReaderBehaviour}
        {ScribeEarnings}
        {ReaderEarnings}
      </>
    );
  } else {
    // default "writer" / Scribe
    body = (
      <>
        {PublishedOutput}
        {Readership}
        {ReaderBehaviour}
        {ScribeEarnings}
      </>
    );
  }

  return (
    <div className="space-y-5" data-testid="journey-writer-stats-panel">
      {MergeBanner}
      {body}
      <ShareScribeMilestone
        profile={data}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
      <StatBreakdownModal
        userId={userId}
        kind={breakdownKind}
        open={!!breakdownKind}
        onOpenChange={(o) => { if (!o) setBreakdownKind(null); }}
      />
    </div>
  );
};

export default WriterStatsPanel;
